import { useEffect, useState } from "react";
import axios from "axios";
import { GALLERY, GALLERY_FILTERS } from "@/data";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Gallery = () => {
  const [active, setActive] = useState("All Work");
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) =>
        setUploaded(
          data.map((p) => ({
            src: `${API}/gallery/file/${p.id}`,
            title: p.title,
            category: p.category,
            details: p.details,
          }))
        )
      )
      .catch(() => {});
  }, []);

  const allItems = [...GALLERY, ...uploaded];
  const filters = [...new Set([...GALLERY_FILTERS, ...uploaded.map((i) => i.category)])];
  const items = active === "All Work" ? allItems : allItems.filter((g) => g.category === active);

  return (
    <section id="gallery" className="bg-[#F8FAFC] py-24 overflow-hidden" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#D97706]">
              Our Work
            </span>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#16283C] mt-3 uppercase">
              Fresh from the Field
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter}
                data-testid="gallery-filter-button"
                onClick={() => setActive(filter)}
                className={`text-xs sm:text-sm font-semibold px-4 py-2 rounded-full border transition-[background-color,color,border-color] duration-200 ${
                  active === filter
                    ? "bg-[#16283C] text-white border-[#16283C]"
                    : "bg-white text-slate-600 border-[#E2E8F0] hover:border-[#F5A623] hover:text-[#D97706]"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        <Carousel
          key={active + items.length}
          opts={{ align: "start", loop: items.length > 3 }}
          className="w-full"
          data-testid="gallery-carousel"
        >
          <CarouselContent className="-ml-5">
            {items.map((item) => (
              <CarouselItem key={item.src} className="pl-5 sm:basis-1/2 lg:basis-1/3">
                <figure
                  data-testid="gallery-item"
                  className="group relative overflow-hidden rounded-2xl h-80 select-none"
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1827]/80 via-transparent to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-5 pt-10 bg-gradient-to-t from-[#0C1827]/95 via-[#0C1827]/50 to-transparent">
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#F5A623]">
                      {item.category}
                    </span>
                    <p className="font-display font-bold text-white text-base mt-1">{item.title}</p>
                    {item.details && (
                      <p className="text-xs text-slate-300 mt-0.5">{item.details}</p>
                    )}
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-3 mt-10">
            <CarouselPrevious
              data-testid="gallery-carousel-prev"
              className="static translate-y-0 h-11 w-11 rounded-full border-[#16283C]/30 bg-transparent text-[#16283C] hover:bg-[#F5A623] hover:text-[#0C1827] hover:border-[#F5A623]"
            />
            <CarouselNext
              data-testid="gallery-carousel-next"
              className="static translate-y-0 h-11 w-11 rounded-full border-[#16283C]/30 bg-transparent text-[#16283C] hover:bg-[#F5A623] hover:text-[#0C1827] hover:border-[#F5A623]"
            />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
